import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { doc, updateDoc, arrayUnion } from 'firebase/firestore';
import { storage, db } from './firebase';

export interface VideoChunk {
  blob: Blob;
  timestamp: Date;
  chunkIndex: number;
  url?: string;
  path?: string;
}

export interface VideoSession {
  sessionId: string;
  sopLogId: string;
  startTime: Date;
  endTime?: Date;
  chunks: VideoChunk[];
  totalDuration: number;
  status: 'recording' | 'completed' | 'failed';
  storageUrls: string[];
  finalUrl?: string;
  storagePaths?: string[];
  finalPath?: string | null;
}

class VideoRecorder {
  private mediaRecorder: MediaRecorder | null = null;
  private videoChunks: VideoChunk[] = [];
  private currentSession: VideoSession | null = null;
  private chunkDuration = 5000; // 5 seconds per chunk for quicker flushing
  private isRecording = false;
  private selectedMimeType: string | null = null;
  private fileExtension: 'webm' | 'mp4' = 'webm';
  private onDataErrorCount = 0;
  private requestDataOnStop = false;
  private lastChunkSize = 0;

  private pickSupportedMimeType(): void {
    // Prefer higher quality/compat options but gracefully fall back
    const candidates = [
      'video/webm;codecs=vp9',
      'video/webm;codecs=vp8',
      'video/webm',
      'video/mp4;codecs=h264',
      'video/mp4'
    ];

    this.selectedMimeType = null;
    for (const type of candidates) {
      // Some browsers throw if MediaRecorder is undefined
      try {
        if ((window as any).MediaRecorder && MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(type)) {
          this.selectedMimeType = type;
          break;
        }
      } catch (_) {
        // ignore
      }
    }

    // Fallback: if nothing explicitly supported, let browser choose default
    if (!this.selectedMimeType) {
      // Try creating without mimeType. Some browsers still work.
      this.selectedMimeType = '';
    }

    // Decide extension
    if (this.selectedMimeType.includes('mp4')) {
      this.fileExtension = 'mp4';
    } else {
      this.fileExtension = 'webm';
    }
    console.log('[Recorder] pickSupportedMimeType ->', {
      selectedMimeType: this.selectedMimeType || 'default',
      fileExtension: this.fileExtension
    });
  }

  async startRecording(
    videoStream: MediaStream,
    sopLogId: string,
    sessionId: string
  ): Promise<void> {
    try {
      if (!(window as any).MediaRecorder) {
        console.error('MediaRecorder is not supported in this browser');
        throw new Error('MediaRecorder not supported');
      }

      // Ensure there is at least one active video track
      const hasVideo = videoStream.getVideoTracks().some(t => t.enabled);
      if (!hasVideo) {
        console.error('No active video tracks in provided MediaStream');
        throw new Error('No active video tracks');
      }

      this.pickSupportedMimeType();

      this.videoChunks = [];
      this.currentSession = {
        sessionId,
        sopLogId,
        startTime: new Date(),
        chunks: [],
        totalDuration: 0,
        status: 'recording',
        storageUrls: []
      };

      // Create MediaRecorder with best-effort mimeType
      const options: MediaRecorderOptions = {
        // Some browsers crash if mimeType is empty string
        ...(this.selectedMimeType ? { mimeType: this.selectedMimeType } : {}),
        videoBitsPerSecond: 2500000
      };

      try {
        this.mediaRecorder = new MediaRecorder(videoStream, options);
      } catch (err) {
        console.warn('Failed to create MediaRecorder with options, retrying without options', err);
        this.mediaRecorder = new MediaRecorder(videoStream);
      }

      this.mediaRecorder.ondataavailable = async (event) => {
        console.log('[Recorder] ondataavailable size:', event.data?.size, 'type:', event.data?.type);
        if (event.data.size > 0) {
          const chunk: VideoChunk = {
            blob: event.data,
            timestamp: new Date(),
            chunkIndex: this.videoChunks.length
          };
          
          this.videoChunks.push(chunk);
          this.lastChunkSize = event.data.size;
          
          // Upload chunk immediately
          try {
            await this.uploadChunk(chunk, sopLogId, sessionId);
            this.onDataErrorCount = 0;
          } catch (e) {
            this.onDataErrorCount += 1;
            console.error('Upload chunk failed (count=' + this.onDataErrorCount + '):', e);
          }
        }
      };

      this.mediaRecorder.onerror = (e: any) => {
        console.error('MediaRecorder error:', e?.error || e);
      };

      this.mediaRecorder.onstop = async () => {
        // Make one last attempt to flush remaining buffered data
        try {
          if (this.requestDataOnStop && this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
            this.mediaRecorder.requestData();
          }
        } catch (_) {}

        await this.finalizeRecording();
      };

      // Start recording with timeslice for chunked data
      try {
        this.mediaRecorder.start(this.chunkDuration);
      } catch (err) {
        console.warn('Failed to start with timeslice, starting without timeslice', err);
        this.mediaRecorder.start();
        // If not using timeslice, requestData on stop to flush
        this.requestDataOnStop = true;
      }
       this.isRecording = true;
      
      console.log('Video recording started with mimeType:', this.selectedMimeType || 'default');
      console.log('[Recorder] debug:', {
        hasVideo,
        selectedMimeType: this.selectedMimeType || 'default',
        fileExtension: this.fileExtension,
        chunkDuration: this.chunkDuration
      });
    } catch (error) {
      console.error('Error starting video recording:', error);
      throw error;
    }
  }

  private async uploadChunk(
    chunk: VideoChunk,
    sopLogId: string,
    sessionId: string
  ): Promise<void> {
    try {
      const fileName = `sop-videos/${sopLogId}/${sessionId}/chunk_${chunk.chunkIndex}_${Date.now()}.${this.fileExtension}`;
      const storageRef = ref(storage, fileName);
      
      console.log('[Recorder] uploadBytes path:', fileName, 'size:', chunk.blob.size);
      await uploadBytes(storageRef, chunk.blob);
      const downloadURL = await getDownloadURL(storageRef);
      
      chunk.url = downloadURL;
      chunk.path = fileName;
      
      // Update Firestore with the new chunk URL
      console.log('[Recorder] updateDoc sopLogs videoChunks append');
      await updateDoc(doc(db, 'sopLogs', sopLogId), {
        videoChunks: arrayUnion({
          chunkIndex: chunk.chunkIndex,
          url: downloadURL,
          path: fileName,
          timestamp: chunk.timestamp,
          size: chunk.blob.size
        })
      });
      
      console.log(`Chunk ${chunk.chunkIndex} uploaded:`, downloadURL);
    } catch (error) {
      console.error(`Error uploading chunk ${chunk.chunkIndex}:`, error);
      throw error;
    }
  }

  private async finalizeRecording(): Promise<void> {
    if (!this.currentSession) return;
    
    try {
      this.currentSession.endTime = new Date();
      this.currentSession.status = 'completed';
      this.currentSession.totalDuration = 
        this.currentSession.endTime.getTime() - this.currentSession.startTime.getTime();
      
      // Collect URLs and paths in index order
      this.currentSession.storageUrls = this.videoChunks
        .filter(chunk => chunk.url)
        .sort((a, b) => a.chunkIndex - b.chunkIndex)
        .map(chunk => chunk.url!);
      this.currentSession.storagePaths = this.videoChunks
        .filter(chunk => !!chunk.path)
        .sort((a, b) => a.chunkIndex - b.chunkIndex)
        .map(chunk => chunk.path!);
      
      // Also upload a single combined file for reliable playback (especially Safari)
      let finalUrl: string | undefined;
      try {
        if (this.videoChunks.length > 0) {
          const combined = new Blob(this.videoChunks.map(c => c.blob), {
            type: this.selectedMimeType || 'video/webm'
          });
          const finalName = `sop-videos/${this.currentSession.sopLogId}/${this.currentSession.sessionId}/session_final.${this.fileExtension}`;
          const finalRef = ref(storage, finalName);
          await uploadBytes(finalRef, combined);
          finalUrl = await getDownloadURL(finalRef);
          if (this.currentSession) {
            this.currentSession.finalPath = finalName;
          }
        }
      } catch (e) {
        console.warn('[Recorder] Failed to upload combined final file:', e);
      }

      // Update SOP log with final video information
      console.log('[Recorder] finalizeRecording updateDoc videoSession summary');
      await updateDoc(doc(db, 'sopLogs', this.currentSession.sopLogId), {
        videoSession: {
          sessionId: this.currentSession.sessionId,
          startTime: this.currentSession.startTime,
          endTime: this.currentSession.endTime,
          totalDuration: this.currentSession.totalDuration,
          status: this.currentSession.status,
          storageUrls: this.currentSession.storageUrls,
          storagePaths: this.currentSession.storagePaths || [],
          chunkCount: this.videoChunks.length,
          finalUrl: finalUrl || null,
          finalPath: this.currentSession.finalPath || null
        }
      });
      
      console.log('Video recording finalized:', this.currentSession.storageUrls);
    } catch (error) {
      console.error('Error finalizing video recording:', error);
      if (this.currentSession) {
        this.currentSession.status = 'failed';
      }
      throw error;
    }
  }

  stopRecording(): void {
    if (this.mediaRecorder && this.isRecording) {
      try {
        // Proactively request any buffered data before stopping (when no timeslice)
        if (!this.chunkDuration || this.requestDataOnStop) {
          this.mediaRecorder.requestData();
        }
      } catch (_) {}
      try {
        this.mediaRecorder.stop();
      } catch (e) {
        console.error('Error stopping MediaRecorder:', e);
      }
      this.isRecording = false;
      console.log('Video recording stopped');
    }
  }

  pauseRecording(): void {
    if (this.mediaRecorder && this.isRecording) {
      this.mediaRecorder.pause();
      console.log('Video recording paused');
    }
  }

  resumeRecording(): void {
    if (this.mediaRecorder && this.isRecording) {
      this.mediaRecorder.resume();
      console.log('Video recording resumed');
    }
  }

  getRecordingStatus(): { isRecording: boolean; sessionId?: string } {
    return {
      isRecording: this.isRecording,
      sessionId: this.currentSession?.sessionId
    };
  }

  getCurrentSession(): VideoSession | null {
    return this.currentSession;
  }
}

export const videoRecorder = new VideoRecorder(); 